import {
  Alert,
  AlertDescription,
  Box,
  Button,
  HStack,
  Heading,
  Stack,
  StackDivider,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
} from '@chakra-ui/react'
import { useState, useEffect, useCallback, useRef } from 'react'
import { keys, map } from 'lodash'
import MonthlyReportHeader from '../../../components/MonthlyReportHeader'
import MonthlyReportNavButtons from '../../../components/MonthlyReportNavButtons'
import QuestionnaireInterfaceToggle from '../../../components/QuestionnaireInterfaceToggle'
import useCurrentReport, {
  useLastMonthReport,
} from '../../../hooks/useCurrentReport'
import useNavigateNext from '../../../hooks/useNavigateNext'
import useUpdateReportDoc from '../../../hooks/useUpdateReportDoc'
import ValidatedInput from '../../../components/ValidatedInput'
import { isPANASReportComplete } from '../../../utils/reportCompletion'
import { keepNumberInRange } from '../../../utils/dataValidation'
import usePurpleBubblesText from '../../../hooks/usePurpleBubblesText'
import useQuestionnaireInterface from '../../../hooks/useQuestionnaireInterface'
import PurpleBubbleContent from '../../../components/PurpleBubbleContent'
import useReportMonthGuard from '../../../hooks/useReportMonthGuard'

// Import local components and types
import PartCCarousel from './components/PartCCarousel'
import PartBCarousel from './components/PartBCarousel'
import PartACarousel from './components/PartACarousel'
import {
  Answers,
  BasicReport,
  PANASReport,
  basicDefault,
} from './utils/types'
import {
  basicSelectQuestions,
  modifiedPanas,
  partAQuestions,
} from './utils/constants'

const ALLOW_COPY_FROM_LAST_MONTH = false

export default function PANASPage() {
  const isValidated = useReportMonthGuard()
  const toast = useToast()
  const updateReport = useUpdateReportDoc()
  const currentReport = useCurrentReport()
  const [partATotalScore, setPartATotalScore] = useState(0)
  const [partBTotalScore, setPartBTotalScore] = useState(0)
  const [basic, setBasic] = useState<BasicReport>(basicDefault)
  const hasLoadedBasic = useRef(false)
  const purpleBubbleContent = usePurpleBubblesText('Step-3')
  const navNext = useNavigateNext()
  const lastMonthReport = useLastMonthReport()
  const { questionnaireInterface, setQuestionnaireInterface } =
    useQuestionnaireInterface()

  useEffect(() => {
    if (currentReport?.panas?.basic && !hasLoadedBasic.current) {
      setBasic(currentReport.panas.basic)
      hasLoadedBasic.current = true
    }
  }, [currentReport])

  // Function to calculate the total score for Part A
  const calculatePartATotalScore = useCallback(() => {
    const {
      positiveThoughts,
      fearOrWorry,
      forgiveOthers,
      forgiveSelf,
      releaseTrauma,
    } = basic
    return (
      Number(positiveThoughts) -
      Number(fearOrWorry) +
      Number(forgiveOthers) +
      Number(forgiveSelf) +
      Number(releaseTrauma)
    )
  }, [basic])

  // Function to calculate the total score for Part B
  const calculatePartBTotalScore = useCallback(() => {
    return basicSelectQuestions.reduce((totalScore, { name }) => {
      const value = basic[name]
      return totalScore + (value === 'yes' ? 2 : value === 'maybe' ? 1 : 0)
    }, 0)
  }, [basic])

  const partBMaximumScore = basicSelectQuestions.length * 2

  // Update the total score whenever the 'basic' state changes
  useEffect(() => {
    setPartATotalScore(calculatePartATotalScore())
    setPartBTotalScore(calculatePartBTotalScore())
  }, [basic, calculatePartATotalScore, calculatePartBTotalScore])

  if (!isValidated) {
    return null // Don't render anything until month is validated
  }

  const report = {
    main: {
      answers: {},
      positive: 0,
      negative: 0,
      net: 0,
      complete: false,
    },
    supplemental: {
      answers: {},
      positive: 0,
      negative: 0,
      net: 0,
      complete: false,
    },
    modifiedPanas: {
      answers: {},
      positive: 0,
      negative: 0,
      net: 0,
      complete: false,
    },
    ...currentReport?.panas,
    basic,
  }

  const modifiedPanasAnswers = report.modifiedPanas.answers

  const update = async (partial: Partial<PANASReport>) => {
    updateReport({
      panas: {
        ...report,
        ...partial,
      },
    })
  }

  const updateBasicField = (name: keyof BasicReport, value: string) => {
    const updatedBasic = { ...basic, [name]: value }
    setBasic(updatedBasic)
    update({ basic: updatedBasic })
  }

  const setAnswersCallback = (key: keyof PANASReport) => {
    return (answers: Answers) => {
      let positive = 0
      let negative = 0

      map(answers, (value, row) => {
        if (row[row.length - 1] === '+') {
          positive += value
        } else {
          negative += value
        }
      })

      update({
        [key]: {
          answers,
          positive,
          negative,
          net: positive - negative,
          complete: keys(answers).length === modifiedPanas.length,
        },
      })
    }
  }

  const lastMonthPanas = lastMonthReport?.panas

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        if (isPANASReportComplete(report)) {
          navNext()
        } else {
          toast({
            status: 'error',
            title: 'Please answer all questions before continuing.',
          })
        }
      }}
    >
      <Stack spacing={6}>
        <MonthlyReportHeader title='Tell us about your mindset.' />
        <QuestionnaireInterfaceToggle
          onChange={setQuestionnaireInterface}
          value={questionnaireInterface}
        />
        {ALLOW_COPY_FROM_LAST_MONTH &&
          lastMonthPanas?.main.complete &&
          lastMonthPanas.supplemental.complete &&
          !lastMonthPanas.copiedFromLastMonth && (
            <Alert status='info'>
              <Stack>
                <AlertDescription>
                  Since you fully completed PANAS in your previous report, you
                  have the option to skip it for this month. This will copy all
                  of your answers from the previous month.
                </AlertDescription>
                <Box>
                  <Button
                    variant='link'
                    onClick={() => {
                      if (
                        window.confirm(
                          'Copy all your answers from the previous month?'
                        )
                      ) {
                        update({
                          ...lastMonthPanas,
                          copiedFromLastMonth: true,
                        })
                        navNext()
                      }
                    }}
                  >
                    Copy from Previous Month
                  </Button>
                </Box>
              </Stack>
            </Alert>
          )}
        <Stack>
          <Stack spacing={1}>
            {purpleBubbleContent?.text && (
              <PurpleBubbleContent text={purpleBubbleContent.text} />
            )}
            <HStack spacing={3} divider={<StackDivider />}>
              <Heading fontSize='xl'>Part A</Heading>
              <Box>
                <Stat>
                  <StatNumber>{partATotalScore}/400</StatNumber>
                </Stat>
              </Box>
            </HStack>
            {questionnaireInterface === 'new' ? (
              <PartACarousel values={basic} onChange={updateBasicField} />
            ) : (
              partAQuestions.map(({ name, question }) => (
                <ValidatedInput
                  inputWidth='30%'
                  key={name}
                  label={`${question} (0-100%)`}
                  max={100}
                  min={0}
                  onChange={(value) =>
                    updateBasicField(name, value !== null ? value.toString() : '')
                  }
                  value={keepNumberInRange(basic[name])}
                />
              ))
            )}
          </Stack>
        </Stack>
        <Stack>
          <HStack spacing={3} divider={<StackDivider />}>
            <Heading fontSize='xl'>Part B</Heading>
            <Box>
              <Stat>
                <StatNumber>
                  {partBTotalScore}/{partBMaximumScore}
                </StatNumber>
              </Stat>
            </Box>
          </HStack>
          <PartBCarousel
            values={basic}
            onChange={(name, value) => {
              const updatedBasic = { ...basic, [name]: value }
              setBasic(updatedBasic)
              update({ basic: updatedBasic })
            }}
          />
        </Stack>
        <Stack>
          <Heading fontSize='xl'>Part C</Heading>
          <HStack spacing={3} divider={<StackDivider />}>
            <Box>
              <Stat>
                <StatLabel>Positive Affect Score</StatLabel>
                <StatNumber>{report.modifiedPanas.positive}</StatNumber>
              </Stat>
            </Box>
            <Box>
              <Stat>
                <StatLabel>Negative Affect Score</StatLabel>
                <StatNumber>{report.modifiedPanas.negative}</StatNumber>
              </Stat>
            </Box>
          </HStack>
          <PartCCarousel
            items={modifiedPanas}
            answers={modifiedPanasAnswers}
            setAnswers={setAnswersCallback('modifiedPanas')}
          />
        </Stack>
        <MonthlyReportNavButtons />
      </Stack>
    </form>
  )
}
